import { FC, useCallback, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Close, DragIndicator } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ColType, DEFAULT_FIRM_SUMMARY_COLUMNS, SummaryGroupBy } from 'common/types/summaryDefinitions';
import { ValueType, ValueTypeCategory } from 'common/types/summaryDefinitions/valueTypes/valueType';
import { CATEGORY_LABELS, CATEGORY_ORDER } from './columnCategories';


const GROUP_BY_EXCLUDED: Record<SummaryGroupBy, string> = {
  company: 'fund_name',
  fund: 'company_name',
};

interface SortableItemProps {
  id: string;
  label: string;
  isFixed?: boolean;
  showReorder?: boolean;
  onRemove?: () => void;
}

const SortableItem: FC<SortableItemProps> = ({ id, label, isFixed, showReorder = true, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isFixed,
  });

  return (
    <ListItem
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 }}
      secondaryAction={
        !isFixed && (
          <IconButton edge="end" size="small" onClick={onRemove}>
            <Close fontSize="small" />
          </IconButton>
        )
      }
      sx={{ py: 0.5, borderRadius: 1 }}>
      <ListItemIcon
        sx={{ minWidth: 28, cursor: isFixed || !showReorder ? 'default' : 'grab', color: 'text.secondary', display: showReorder ? undefined : 'none' }}
        {...(isFixed || !showReorder ? {} : { ...attributes, ...listeners })}>
        <DragIndicator fontSize="small" />
      </ListItemIcon>
      <ListItemText primary={label} sx={{ variant: 'body2' }} />
      {isFixed && (
        <Typography variant="caption" color="text.disabled" sx={{ mr: 1, flexShrink: 0 }}>
          FIXED
        </Typography>
      )}
    </ListItem>
  );
};

export interface EditColumnsPanelProps {
  availableColumns: ValueType[];
  allValueTypes?: Map<string, ValueType>;
  columns: ColType[];
  requiredColumns?: Set<string>;
  groupBy?: SummaryGroupBy;
  onChange: (columns: ColType[]) => void;
  disabled?: boolean;
  showReorder?: boolean;
}

const EditColumnsPanel: FC<EditColumnsPanelProps> = ({
  availableColumns,
  allValueTypes,
  columns,
  requiredColumns,
  groupBy = 'company',
  onChange,
  disabled,
  showReorder = true,
}) => {
  const [search, setSearch] = useState('');
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const excludedByGroupBy = GROUP_BY_EXCLUDED[groupBy];

  const selectedColumns = useMemo(
    () => columns.filter(c => c !== excludedByGroupBy),
    [columns, excludedByGroupBy]
  );

  const availableForSelection = useMemo(
    () => availableColumns.filter(vt => vt.id !== excludedByGroupBy && !requiredColumns?.has(vt.id)),
    [availableColumns, excludedByGroupBy, requiredColumns]
  );

  const valueTypeById = useMemo(
    () => allValueTypes ?? new Map(availableColumns.map(vt => [vt.id, vt])),
    [allValueTypes, availableColumns]
  );

  const selectedSet = useMemo(() => new Set(selectedColumns), [selectedColumns]);

  const grouped = useMemo(() => {
    const term = search.toLowerCase();
    const map = new Map<ValueTypeCategory, ValueType[]>();
    availableForSelection
      .filter(vt => vt.displayName.toLowerCase().includes(term))
      .forEach(vt => {
        const cat = vt.metadata?.category ?? 'custom';
        if (!map.has(cat)) {
          map.set(cat, []);
        }
        map.get(cat)!.push(vt);
      });
    return CATEGORY_ORDER
      .map(cat => ({ cat, label: CATEGORY_LABELS[cat], items: map.get(cat) ?? [] }))
      .filter(g => g.items.length > 0);
  }, [availableForSelection, search]);

  const sortableIds = useMemo(() => selectedColumns.map((_, i) => `col-${i}`), [selectedColumns]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleToggle = useCallback((id: ColType) => {
    onChange(selectedSet.has(id) ? selectedColumns.filter(c => c !== id) : [...selectedColumns, id]);
  }, [selectedColumns, selectedSet, onChange]);

  const handleRemove = useCallback((index: number) => {
    onChange(selectedColumns.filter((_, i) => i !== index));
  }, [selectedColumns, onChange]);

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setActiveDragId(String(e.active.id));
  }, []);

  const handleDragEnd = useCallback((e: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) {
      return;
    }
    onChange(arrayMove(selectedColumns, sortableIds.indexOf(String(active.id)), sortableIds.indexOf(String(over.id))));
  }, [selectedColumns, sortableIds, onChange]);

  const handleReset = useCallback(() => {
    onChange((DEFAULT_FIRM_SUMMARY_COLUMNS as ColType[]).filter(c => c !== excludedByGroupBy));
  }, [excludedByGroupBy, onChange]);

  const activeDragLabel = useMemo(() => {
    if (!activeDragId) {
      return null;
    }
    const col = selectedColumns[sortableIds.indexOf(activeDragId)];
    return valueTypeById.get(col)?.displayName ?? col;
  }, [activeDragId, sortableIds, selectedColumns, valueTypeById]);

  return (
    <Stack direction="row" sx={{ height: 480, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ width: '50%', borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search columns..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }} subheader={<li />}>
          {grouped.map(group => (
            <li key={group.cat}>
              <ul style={{ padding: 0, margin: 0 }}>
                <ListSubheader sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'text.secondary',
                  lineHeight: '28px',
                  backgroundColor: 'background.paper',
                }}>
                  {group.label}
                </ListSubheader>
                {group.items.map(vt => {
                  const isSelected = selectedSet.has(vt.id as ColType);
                  return (
                    <Box
                      key={vt.id}
                      component="li"
                      onClick={() => !disabled && handleToggle(vt.id as ColType)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 0.25,
                        px: 1.5,
                        cursor: disabled ? 'default' : 'pointer',
                        opacity: disabled ? 0.5 : 1,
                        backgroundColor: isSelected ? 'action.selected' : 'transparent',
                        '&:hover': { backgroundColor: isSelected ? 'action.selected' : 'action.hover' },
                      }}>
                      <Checkbox size="small" checked={isSelected} disableRipple sx={{ p: 0.5, mr: 1 }} />
                      <Typography variant="body2">{vt.displayName}</Typography>
                    </Box>
                  );
                })}
              </ul>
            </li>
          ))}
        </List>
      </Box>

      <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Selected ({selectedColumns.length})</Typography>
            <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }} onClick={handleReset}>
              Reset to defaults
            </Typography>
          </Stack>
          {showReorder && <Typography variant="caption" color="text.secondary">Drag to reorder. &quot;{groupBy === 'fund' ? 'Fund' : 'Company'}&quot; column is always first.</Typography>}
        </Box>
        <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}>
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              {selectedColumns.map((colType, i) => {
                const isFixed = requiredColumns?.has(colType);
                const nonRequiredCount = selectedColumns.filter(c => !requiredColumns?.has(c)).length;
                return (
                  <SortableItem
                    key={`${colType}-${i}`}
                    id={`col-${i}`}
                    label={valueTypeById.get(colType)?.displayName ?? colType}
                    isFixed={isFixed || (!isFixed && nonRequiredCount <= 1)}
                    showReorder={showReorder}
                    onRemove={() => handleRemove(i)}
                  />
                );
              })}
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeDragLabel ? (
                <Paper elevation={4} sx={{ px: 2, py: 1, borderRadius: 1, transform: 'rotate(1deg)' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <DragIndicator fontSize="small" color="action" />
                    <Typography variant="body2">{activeDragLabel}</Typography>
                  </Stack>
                </Paper>
              ) : null}
            </DragOverlay>
          </DndContext>
        </List>
      </Box>
    </Stack>
  );
};

export default EditColumnsPanel;
